import React from 'react';
import TextField from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import moment from 'moment';
import '../../assets/style/loanForm.scss';

// Building blocks for the Add / Edit loan form: card sections on a 12-column
// grid, and fields with the label above, one height, an inline error and
// optional unit adornments (display only — values are unchanged).
// `span` = columns the field takes (3, 4, 6 or 12).

export function FormSection({ title, icon, children }) {
  return (
    <section className='lf-section'>
      <h3>
        {icon ? <span className='lf-section-icon'>{icon}</span> : null}
        {title}
      </h3>
      <div className='lf-grid'>{children}</div>
    </section>
  );
}

function FieldShell({
  id, label, required, error, span = 6, children,
}) {
  return (
    <div className={`lf-field lf-span-${span}`}>
      <label htmlFor={id} className='lf-label'>
        {label}
        {required ? <span className='lf-req' aria-hidden='true'> *</span> : null}
      </label>
      {children}
      {error ? <div className='lf-error' role='alert'>{error}</div> : null}
    </div>
  );
}

const adornments = (prefix, suffix) => ({
  startAdornment: prefix ? <InputAdornment position='start'>{prefix}</InputAdornment> : undefined,
  endAdornment: suffix ? <InputAdornment position='end'>{suffix}</InputAdornment> : undefined,
});

export function TextInput({
  id, label, required, error, span, value, onChange, type, multiline, rows, prefix, suffix,
  disabled,
}) {
  return (
    <FieldShell id={id} label={label} required={required} error={error} span={span}>
      <TextField
        id={id}
        variant='outlined'
        size='small'
        fullWidth
        type={type}
        multiline={multiline}
        rows={rows}
        disabled={disabled}
        value={value === undefined || value === null ? '' : value}
        onChange={onChange}
        error={Boolean(error)}
        InputProps={adornments(prefix, suffix)}
      />
    </FieldShell>
  );
}

// Native select; the empty option keeps the old '#78' value so the existing
// change handlers still map it to "none".
export function SelectInput({
  id, label, required, error, span, value, onChange, options, disabled,
}) {
  return (
    <FieldShell id={id} label={label} required={required} error={error} span={span}>
      <TextField
        id={id}
        select
        variant='outlined'
        size='small'
        fullWidth
        SelectProps={{ native: true }}
        disabled={disabled}
        value={value === undefined || value === null ? '#78' : value}
        onChange={onChange}
        error={Boolean(error)}
      >
        <option value='#78'>Select…</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </TextField>
    </FieldShell>
  );
}

export function DateInput({
  id, label, required, error, span, value, onChange,
}) {
  return (
    <FieldShell id={id} label={label} required={required} error={error} span={span}>
      <MuiPickersUtilsProvider utils={MomentUtils}>
        <KeyboardDatePicker
          id={id}
          disableToolbar
          autoOk
          variant='inline'
          inputVariant='outlined'
          size='small'
          fullWidth
          format='DD-MM-YYYY'
          placeholder='DD-MM-YYYY'
          value={value ? moment(value, 'DD-MM-YYYY HH:mm:ss') : null}
          onChange={onChange}
          error={Boolean(error)}
          helperText={null}
        />
      </MuiPickersUtilsProvider>
    </FieldShell>
  );
}

// Wraps any other control (e.g. the branch autocomplete) in the same shell.
export function CustomField({
  id, label, required, error, span, children,
}) {
  return (
    <FieldShell id={id} label={label} required={required} error={error} span={span}>
      {children}
    </FieldShell>
  );
}
